import {
  FormEvent,
  Fragment,
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { useHistory } from 'react-router'
import { Input, Button, toast, Link } from '@conexasaude/hero'

import Icon from '@/components/HeroIcons'
import { PasswordTips } from '../PasswordTips'

import services from '../../auth-services'
import { checkPasswordStrength } from '@/utils/password'
import { removeCache } from '@/utils/cache'
import { useStyles } from './useStyles'

import {
  PasswordStrength,
  PasswordStrengthBar,
} from '@/components/PasswordStrengthBar'
import { useChatLayerStore } from '@/components/ChatLayer'

const ChangePasswordForm: FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [password, setPassword] = useState<string>()
  const [confirmPassword, setConfirmPassword] = useState<string>()
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>()
  const [passwordStrengthMessage, setPasswordStrengthMessage] =
    useState<string>()
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false)

  const history = useHistory()

  const { container, inputIcon, LinkSupport } = useStyles()
  const openChatLayer = useChatLayerStore(state => state.openChatLayer)

  const changePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsFormSubmitted(true)
    setIsLoading(true)

    if (passwordStrength !== 'strong') {
      toast(
        'Sua senha está fraca, siga nossas dicas para uma senha mais segura',
        {
          variant: 'danger',
          autoClose: 5000,
        },
      )
      setIsLoading(false)

      return
    }

    if (password !== confirmPassword) {
      setIsLoading(false)

      return
    }

    try {
      await services.changePassword(password, confirmPassword)

      removeCache('passwordConfirmed')

      history.push('/trocar-senha/sucesso', {
        passwordChanged: true,
      })
    } catch (err) {
      const errMessage =
        err?.response?.data?.object?.msg ||
        err?.response?.data?.msg ||
        err.message

      toast(errMessage, {
        variant: 'danger',
        autoClose: 5000,
      })
    }

    setIsLoading(false)
  }

  const chossePasswordIcon = (): ReactNode => {
    return showPassword ? (
      <Icon
        name="eye-off"
        themeColor={['extra', 'gray_icon']}
        onClick={() => setShowPassword(false)}
      />
    ) : (
      <Icon
        name="eye-on"
        themeColor={['extra', 'gray_icon']}
        onClick={() => setShowPassword(true)}
      />
    )
  }

  const chooseConfirmPasswordIcon = (): ReactNode => {
    return showConfirmPassword ? (
      <Icon
        name="eye-off"
        themeColor={['extra', 'gray_icon']}
        onClick={() => setShowConfirmPassword(false)}
      />
    ) : (
      <Icon
        name="eye-on"
        themeColor={['extra', 'gray_icon']}
        onClick={() => setShowConfirmPassword(true)}
      />
    )
  }

  useEffect(() => {
    if (password) {
      const strength = checkPasswordStrength(password)

      setPasswordStrength(strength)

      if (strength === 'weak')
        setPasswordStrengthMessage('Senha fraca, utilize nossas dicas de senha')

      if (strength === 'medium')
        setPasswordStrengthMessage(
          'Sua senha pode melhorar, utilize nossas dicas de senha',
        )

      if (strength === 'strong') {
        setPasswordStrengthMessage('Sua senha está ótima!')
      }

      return
    }

    setPassword(null)
    setPasswordStrength(null)
    setPasswordStrengthMessage(null)
  }, [password])

  return (
    <Fragment>
      <form css={container} onSubmit={changePassword}>
        <h2>Criar nova senha</h2>
        <p>Dicas para criar uma senha forte e segura:</p>

        <PasswordTips password={password} />

        <Input
          label="Nova senha"
          placeholder="Informe a nova senha"
          inputProps={{ type: showPassword ? 'text' : 'password' }}
          onChange={event => setPassword(event.target.value)}
          required
          validated={isFormSubmitted && passwordStrength === 'strong'}
          icon={chossePasswordIcon()}
          styles={{ icon: inputIcon }}
          fullWidth
        />
        <PasswordStrengthBar
          strength={passwordStrength}
          message={passwordStrengthMessage}
        />

        <Input
          label="Confirmar nova senha"
          placeholder="Confirme a nova senha"
          inputProps={{ type: showConfirmPassword ? 'text' : 'password' }}
          onChange={event => setConfirmPassword(event.target.value)}
          required
          validated={
            isFormSubmitted &&
            passwordStrength === 'strong' &&
            password === confirmPassword
          }
          error={isFormSubmitted && password !== confirmPassword}
          errorMessage="As senhas estão diferentes"
          icon={chooseConfirmPasswordIcon()}
          styles={{ icon: inputIcon }}
          fullWidth
        />

        <Button size="md" busy={isLoading} disabled={isLoading} fullWidth>
          Criar senha
        </Button>
      </form>

      <Link css={LinkSupport} onClick={openChatLayer}>
        Precisa de ajuda?
      </Link>
    </Fragment>
  )
}

export { ChangePasswordForm }
